import React, {
    useMemo,
    createRef,
    useRef,
    useCallback,
    useEffect,
    useState,
} from 'react';
import UrlUtils from '@spothero/utils/url';
import {useDispatch, useSelector} from 'react-redux';
import {Box, Flex, Container, Text, Icon, Button, Alert} from '@spothero/ui';
import IconSpothero from '@spothero/icons/spothero';
import {useReactToPrint} from 'react-to-print';
import {useHistory} from 'react-router';
import ReservationUtils from 'utils/reservation-utils';
import {Helmet} from 'react-helmet-async';
import {PROPTYPE_RESERVATION} from '@/proptypes/index';
import dayjs from 'utils/dayjs-timezone';
import Redemption from '../redemption';
import VehicleDetails from '../vehicle-details';
import PaymentDetails from '../payment-details';
import DateTimeDetails from '../date-time-details';
import MonthlyDateTimeDetails from '../monthly-date-time-details';
import Header from '../header';
import CancelReservation from '../cancel-reservation';
import ChangeVehicleModalWrapper from '../change-vehicle-modal-wrapper';
import GettingThere from '../getting-there';
import ParkingInstructions from '../parking-instructions';
import PhoneDetails from '../phone-details';
import AppStoreBadges from 'common/app-store-badges';
import {
    rightColumnItemStyleProps,
    leftColumnItemStyleProps,
} from 'pages/parking-pass/styles';
import ChangeReservation from '../change-reservation';
import CallHeroes from '../call-heroes';
import Amenities from '../amenities';
import ThingsYouShouldKnow from '../things-you-should-know';
import ShuttleInformation from '../shuttle-information/ShuttleInformation';
import LocationAccessHours from '../location-access-hours';
import SpotImages from 'common/spot-details/spot-images';
import useMobileBreakPoint from 'hooks/use-mobile-breakpoint';
import {scrollToRef} from 'utils/dom-utils';
import trackParkingPassClick from 'segment/events/parking-pass-click';
import throttle from 'lodash/throttle';
import MobileTabs from '../mobile-tabs';
import {CarouselVariant} from 'common/image-carousel';
import {replace} from 'store/router/router-actions';
import {toggleModal} from 'store/parking-pass/parking-pass-actions';
import ParkedCarProtectionDetails from '../parked-car-protection-details';
import {CAR_PROTECTION_ADDON_TYPE} from 'types/rental-addons';
import {
    CHANGE_PHONE,
    CHANGE_TIMES,
    CHANGE_VEHICLE,
    REFUND,
} from 'store/parking-pass/parking-pass-reducer';
import trackParkingPassPage from 'segment/pages/parking-pass';

const printedPageStyles = `
    @media print {
        body {
            -webkit-print-color-adjust: exact;
        }
    }

    @page {
        size: auto;
        margin: 5mm 4mm 5mm 4mm;
    }
`;

const ParkingPassContent = ({reservation}) => {
    const redemptionType = useMemo(() => {
        if (reservation.displayBarcode) {
            // if there is a QR code or barcode use it for redemption
            return ReservationUtils.PARKING_PASS_REDEMPTION_TYPE.SCAN_CODE;
        } else if (
            ReservationUtils.LICENSE_PLATE_REDEMPTION_TYPES.includes(
                reservation.parkingPassType
            ) &&
            reservation.vehicle &&
            Object.keys(reservation.vehicle).length > 0
        ) {
            // if the parking pass type likely uses display ID for redemption
            return ReservationUtils.PARKING_PASS_REDEMPTION_TYPE.LICENSE_PLATE;
        } else {
            // last just default to just showing the display ID
            return ReservationUtils.PARKING_PASS_REDEMPTION_TYPE.DISPLAY_ID;
        }
    }, [
        reservation.displayBarcode,
        reservation.parkingPassType,
        reservation.vehicle,
    ]);

    const isMobile = useMobileBreakPoint();
    const parkingInstructionsRef = createRef();
    const gettingThereRef = useRef();
    const dispatch = useDispatch();
    const toPrintRef = useRef();
    const pageCallMade = useRef(false);
    const handlePrint = useReactToPrint({
        content: () => toPrintRef.current,
        pageStyle: printedPageStyles,
    });
    const user = useSelector(state => state.user.data);
    const [activeTab, setActiveTab] = useState(
        ReservationUtils.ACTIVE_TAB_VALUES.PASS
    );

    const history = useHistory();

    useEffect(() => {
        // only add event listener for mobile tabs if it's mobile
        if (isMobile) {
            const parkingInstructionsOffset =
                parkingInstructionsRef.current.offsetTop;
            const gettingThereOffset = gettingThereRef.current.offsetTop;

            const handleScroll = () => {
                // this uses 66 instead of 64 to give a little bit of a buffer in case things end up off by a pixel or two
                setActiveTab(
                    window.scrollY >= parkingInstructionsOffset - 66
                        ? ReservationUtils.ACTIVE_TAB_VALUES.HOW_TO
                        : window.scrollY >= gettingThereOffset - 66
                        ? ReservationUtils.ACTIVE_TAB_VALUES.DETAILS
                        : ReservationUtils.ACTIVE_TAB_VALUES.PASS
                );
            };

            window.addEventListener('scroll', throttle(handleScroll, 200));

            return () => {
                window.removeEventListener(
                    'scroll',
                    throttle(handleScroll, 200)
                );
            };
        }
    }, [parkingInstructionsRef, gettingThereRef, isMobile]);

    const activeStatus = useMemo(() => {
        const currentTime = dayjs.utc(dayjs()).tz(reservation.timezone);

        if (currentTime < reservation.starts) {
            return ReservationUtils.ACTIVE_STATUS.NOT_STARTED;
        } else if (currentTime > reservation.ends) {
            return ReservationUtils.ACTIVE_STATUS.ENDED;
        }

        return ReservationUtils.ACTIVE_STATUS.ACTIVE;
    }, [reservation.starts, reservation.ends, reservation.timezone]);

    useEffect(() => {
        if (pageCallMade.current === false) {
            trackParkingPassPage({
                reservation,
                user,
                activeStatus,
                redemptionType,
            });

            pageCallMade.current = true;
        }
    }, [reservation, user, activeStatus, redemptionType]);

    const handleTrackedClick = useCallback(
        ({clickTarget, isReservationChange}) => {
            trackParkingPassClick({
                reservation,
                user,
                clickTarget,
                isReservationChange,
                activeStatus,
            });
        },
        [reservation, user, activeStatus]
    );

    const handleInstructionsClick = (location = '"How To" on Mobile Nav') => {
        handleTrackedClick({
            clickTarget: location,
            isReservationChange: false,
        });
        scrollToRef(parkingInstructionsRef);
    };
    const handleGettingThereClick = () => {
        handleTrackedClick({
            clickTarget: '"Details" on Mobile Nav',
            isReservationChange: false,
        });
        scrollToRef(gettingThereRef);
    };
    const handleToTopClick = () => {
        handleTrackedClick({
            clickTarget: '"Pass" on Mobile Nav',
            isReservationChange: false,
        });
        scrollToRef(toPrintRef);
    };

    const {redemptionInstructions} = reservation.facility;
    const {
        paymentStatus,
        vehicleRequired,
        vehicle,
        reservationType,
        airport,
        entryTimestamp,
        facility: {allowUpdatingReservationAfterEntry, phoneNumberRequired},
        addOns,
    } = reservation;

    // I know, it's a terrible variable name. It is true when a facility doesn't allow changes after a vehicle has entered the facility and we have an entry time from the user showing they have, in fact, entered.
    const enteredAndCantChange =
        !allowUpdatingReservationAfterEntry && entryTimestamp;

    const isAirport =
        reservationType === ReservationUtils.RESERVATION_TYPES.AIRPORT;
    const canEdit =
        paymentStatus !== ReservationUtils.PAYMENT_STATUSES.REFUND &&
        activeStatus !== ReservationUtils.ACTIVE_STATUS.ENDED;
    const arrivalInstructions = airport?.arrivalInstructions;
    const departureInstructions = airport?.departureInstructions;
    const carProtectionAddOn = addOns?.find(
        addOn => addOn.type === CAR_PROTECTION_ADDON_TYPE
    );

    // If changeReservation in the search params, open relevant modal if allowed, then remove changeReservation from url
    useEffect(() => {
        const urlParams = UrlUtils.parseParams(history.location.search);
        const changeReservation = urlParams.changeReservation;

        if (!changeReservation) {
            return;
        }

        switch (changeReservation) {
            case REFUND:
                dispatch(toggleModal({modal: REFUND}));
                break;
            case CHANGE_TIMES:
                dispatch(toggleModal({modal: CHANGE_TIMES}));
                break;
            case CHANGE_PHONE:
                if (
                    canEdit &&
                    (phoneNumberRequired ||
                        reservationType ===
                            ReservationUtils.RESERVATION_TYPES.MONTHLY)
                ) {
                    dispatch(toggleModal({modal: CHANGE_PHONE}));
                }
                break;
            case CHANGE_VEHICLE:
                if (!enteredAndCantChange && canEdit && vehicleRequired) {
                    dispatch(toggleModal({modal: CHANGE_VEHICLE}));
                }
                break;
            default:
                break;
        }

        const newParams = {...urlParams};

        delete newParams.changeReservation;
        const newSearch = UrlUtils.createQueryString(newParams);

        dispatch(
            replace(`${history.location.pathname}?${newSearch}`, {
                reload: false,
                method: 'replace',
            })
        );
    }, [
        dispatch,
        enteredAndCantChange,
        canEdit,
        phoneNumberRequired,
        reservationType,
        vehicleRequired,
        history,
    ]);

    return (
        <Box
            ref={toPrintRef}
            sx={{
                '@media print': {
                    marginX: 4,
                },
                scrollMarginTop: {
                    base: '4rem',
                    tablet: 0,
                },
            }}
            as="main"
        >
            <Helmet>
                <meta name="robots" content="noindex, nofollow" />
                <body className="standalone-page" />
            </Helmet>

            {/* Banner on printer page */}
            <Box
                sx={{
                    '@media print': {
                        display: 'flex',
                        marginX: -4,
                        bgColor: 'primary.default',
                        paddingY: 1,
                        paddingLeft: 8,
                        marginBottom: 1,
                    },
                }}
                display="none"
            >
                <Icon width="10" height="10" as={IconSpothero} fill="white" />
            </Box>

            <Box
                bgColor="primary.default"
                sx={{
                    '@media print': {
                        bgColor: 'white',
                        paddingBottom: 6,
                    },
                }}
                paddingTop={4}
                paddingBottom={{base: 12, tablet: 40}}
            >
                <Container maxWidth={{tablet: '100%'}}>
                    <Header
                        title={reservation.facility.title}
                        displayId={reservation.displayId}
                        redemptionType={redemptionType}
                        event={reservation.event}
                        partnerName={reservation.partner?.displayName}
                    />
                </Container>
            </Box>

            <Box
                paddingBottom={{base: 0, tablet: 8}}
                paddingX={{base: 0, tablet: 4}}
            >
                <Flex
                    flexDirection={{base: 'column', desktop: 'row'}}
                    alignItems={{base: 'center', desktop: 'stretch'}}
                    width="100%"
                    marginX="auto"
                    boxShadow={{base: 'none', tablet: 'lg'}}
                    maxWidth="1120px"
                    marginTop={{base: -2, tablet: -36}}
                    sx={{
                        '@media print': {
                            flexDirection: 'row',
                            alignItems: 'stretch',
                            borderWidth: '1px',
                            borderColor: 'gray.100',
                            borderRadius: 'md',
                            boxShadow: 'none',
                            marginTop: 0,
                        },
                    }}
                >
                    <Redemption
                        reservation={reservation}
                        redemptionType={redemptionType}
                        activeStatus={activeStatus}
                        onInstructionsClick={handleInstructionsClick}
                        onPrintClick={handlePrint}
                        onTrackedClick={handleTrackedClick}
                    />
                    <Flex
                        width="100%"
                        flex={{base: null, desktop: 1}}
                        backgroundColor="gray.light"
                        borderTopRightRadius={{base: 0, desktop: 'md'}}
                        borderBottomRightRadius={{base: 0, tablet: 'md'}}
                        borderBottomLeftRadius={{
                            base: 0,
                            tablet: 'md',
                            desktop: '0',
                        }}
                        alignItems="center"
                        paddingTop={{base: 0, tablet: 6}}
                        paddingBottom={{base: 0, tablet: 6}}
                        flexDirection="column"
                        sx={{
                            '@media print': {
                                padding: 3,
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'white',
                                borderBottomRightRadius: 'md',
                                borderTopRightRadius: 'md',
                                flex: 1,
                            },
                        }}
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            flex={1}
                            width="100%"
                            flexDirection="column"
                        >
                            <Flex
                                flexDirection="column"
                                backgroundColor="white"
                                borderRadius={{base: 'none', tablet: 'md'}}
                                borderWidth={{base: '0', tablet: '1px'}}
                                borderTopWidth="1px"
                                borderColor="gray.100"
                                width={{base: '100%', tablet: '440px'}}
                                position="relative"
                                sx={{
                                    '@media print': {
                                        borderRadius: 'md',
                                        borderWidth: '1px',
                                    },
                                }}
                            >
                                {reservation.reservationType ===
                                ReservationUtils.RESERVATION_TYPES.MONTHLY ? (
                                    <MonthlyDateTimeDetails
                                        starts={reservation.starts}
                                        ends={reservation.ends}
                                    />
                                ) : (
                                    <DateTimeDetails
                                        reservation={reservation}
                                        activeStatus={activeStatus}
                                        paymentStatus={paymentStatus}
                                        onTrackedClick={handleTrackedClick}
                                        enteredAndCantChange={
                                            enteredAndCantChange
                                        }
                                    />
                                )}

                                <VehicleDetails
                                    activeStatus={activeStatus}
                                    paymentStatus={paymentStatus}
                                    vehicleRequired={vehicleRequired}
                                    vehicle={vehicle}
                                    enteredAndCantChange={enteredAndCantChange}
                                    partner={reservation.partner}
                                />
                                {carProtectionAddOn && (
                                    <ParkedCarProtectionDetails
                                        carProtectionAddOn={carProtectionAddOn}
                                        onTrackedClick={handleTrackedClick}
                                    />
                                )}
                                <PhoneDetails
                                    reservation={reservation}
                                    activeStatus={activeStatus}
                                    paymentStatus={paymentStatus}
                                    onTrackedClick={handleTrackedClick}
                                />
                                <PaymentDetails reservation={reservation} />

                                {reservation.facility.oversizeSupported &&
                                reservation.partner &&
                                !reservation.partner.updateVehicleEnabled ? (
                                    <Box padding={4} paddingTop={0}>
                                        <Alert
                                            status="neutral"
                                            title="Oversize fee charge onsite"
                                            description="If you drive an oversize vehicle, additional fee might be charged on site by the parking facility."
                                            showIcon
                                            color="text.primary.light"
                                        />
                                    </Box>
                                ) : null}

                                <CancelReservation
                                    activeStatus={activeStatus}
                                    reservation={reservation}
                                    onTrackedClick={handleTrackedClick}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>

                <Box
                    marginTop={isMobile ? 0 : 10}
                    maxWidth="1120px"
                    marginX="auto"
                    sx={{
                        '@media print': {
                            marginTop: 10,
                            button: {
                                display: 'none',
                            },
                        },
                    }}
                >
                    <SpotImages
                        carouselVariant={
                            isMobile
                                ? CarouselVariant.SINGLE
                                : CarouselVariant.WIDE
                        }
                        images={reservation.facility.images}
                        alt={reservation.facility.title}
                        carouselHeight={isMobile ? 16 : 17}
                        urlProperty="urlTemplate"
                        logos={airport?.logos}
                    />
                </Box>

                <Flex
                    flexDirection={{base: 'column', desktop: 'row'}}
                    alignItems={{base: 'center', desktop: 'stretch'}}
                    width="100%"
                    marginX="auto"
                    maxWidth="1120px"
                    marginTop={10}
                    sx={{
                        '@media print': {
                            flexDirection: 'row',
                            alignItems: 'stretch',
                        },
                    }}
                >
                    <Flex
                        flexDirection="column"
                        width="100%"
                        flex={{base: null, desktop: 1}}
                        marginRight={{base: 0, desktop: 32}}
                        marginBottom={0}
                        sx={{
                            '@media print': {
                                flex: 1,
                                marginRight: 8,
                            },
                        }}
                    >
                        <Box
                            {...leftColumnItemStyleProps}
                            sx={{
                                scrollMarginTop: {
                                    base: '4rem',
                                    tablet: 0,
                                },
                            }}
                            ref={gettingThereRef}
                        >
                            <GettingThere
                                onTrackedClick={handleTrackedClick}
                                facility={reservation.facility}
                            />
                        </Box>
                        <ThingsYouShouldKnow
                            restrictions={reservation.facility.restrictions}
                        />
                        {/* Would have preferred putting ref in ParkingInstructions but weirdness with forwardRef + our ssr + useMemo */}
                        <Box
                            {...leftColumnItemStyleProps}
                            sx={{
                                scrollMarginTop: {
                                    base: '4rem',
                                    tablet: 0,
                                },
                            }}
                            ref={parkingInstructionsRef}
                        >
                            {isAirport ? (
                                <>
                                    <Box marginBottom={8}>
                                        <ParkingInstructions
                                            instructions={arrivalInstructions}
                                            subHeading="Before you take off"
                                        />
                                    </Box>
                                    <ParkingInstructions
                                        instructions={departureInstructions}
                                        subHeading="Once you return"
                                        showHeading={false}
                                    />
                                </>
                            ) : (
                                <ParkingInstructions
                                    instructions={redemptionInstructions}
                                />
                            )}
                        </Box>
                        {Boolean(reservation.monthlyAgreementLink) && (
                            <Box>
                                <Button
                                    as="a"
                                    href={reservation.monthlyAgreementLink}
                                >
                                    Download Application Form
                                </Button>
                            </Box>
                        )}
                    </Flex>
                    <Flex
                        flexDirection="column"
                        width={{base: '100%', desktop: '350px'}}
                        sx={{
                            '@media print': {
                                width: '350px',
                            },
                        }}
                    >
                        <Box
                            as="aside"
                            bgColor={{
                                base: 'transparent',
                                desktop: 'gray.light',
                            }}
                            borderRadius={{base: 'none', tablet: 'lg'}}
                            sx={{
                                '@media print': {
                                    borderRadius: 'lg',
                                },
                            }}
                        >
                            {isAirport && (
                                <ShuttleInformation
                                    shuttle={airport?.shuttle}
                                    details={airport?.details}
                                />
                            )}
                            <LocationAccessHours
                                accessHours={reservation.accessHours}
                                accessHoursComment={
                                    reservation.accessHoursComment
                                }
                            />
                            <Amenities
                                amenities={reservation.facility.amenitiesFull}
                                monthlyInOutDetail={
                                    reservation.monthlyInOutDetail
                                }
                                reservationType={reservation.reservationType}
                            />
                            <ChangeReservation
                                activeStatus={activeStatus}
                                reservation={reservation}
                                onTrackedClick={handleTrackedClick}
                            />
                            <CallHeroes
                                rentalId={reservation.rentalId}
                                onTrackedClick={handleTrackedClick}
                                phoneNumber={reservation.partner?.phoneNumber}
                                phoneNumberFormatted={
                                    reservation.partner?.phoneNumberFormatted
                                }
                            />
                            {/* Hide this on mobile because same badges in footer right under it */}
                            {!isMobile && (
                                <Flex
                                    backgroundColor="gray.light"
                                    {...rightColumnItemStyleProps}
                                >
                                    <Text marginBottom={4}>
                                        For the smoothest parking experience,
                                        try the SpotHero mobile app.
                                    </Text>
                                    <AppStoreBadges />
                                </Flex>
                            )}
                        </Box>
                    </Flex>
                </Flex>
            </Box>
            {isMobile && (
                <MobileTabs
                    activeTab={activeTab}
                    onToTopClick={handleToTopClick}
                    onGettingThereClick={handleGettingThereClick}
                    onInstructionsClick={handleInstructionsClick}
                />
            )}
            <ChangeVehicleModalWrapper
                reservation={reservation}
                activeStatus={activeStatus}
                onTrackedClick={handleTrackedClick}
            />
        </Box>
    );
};

ParkingPassContent.propTypes = {
    reservation: PROPTYPE_RESERVATION.isRequired,
};

export default ParkingPassContent;
